import { array } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { multiplyNumbersAsDecimals, sumNumbersAsDecimals } from '@mews/framework';
import { CurrencyAcceptance } from '@mews-commander/data-access';
import { AddProductFormValues, ProductOrderFormValues, ServiceOrderFormField } from './schema';

export const getCostInDefaultCurrency = (
    unitCost: number,
    unitCurrency: string,
    exchangeRates?: CurrencyAcceptance[]
) => {
    const currentExchangeRate = exchangeRates?.find((r) => r.currencyCode === unitCurrency);

    if (!currentExchangeRate) return unitCost;

    return multiplyNumbersAsDecimals(unitCost, currentExchangeRate.conversionRateToDefault);
};

export const getTotalAmount = ({
    hasSingleCurrency,
    formValues,
    exchangeRates,
    serviceCurrency,
}: {
    hasSingleCurrency: boolean;
    formValues: Pick<AddProductFormValues, ServiceOrderFormField.ProductOrders | ServiceOrderFormField.Value>;
    exchangeRates?: CurrencyAcceptance[];
    serviceCurrency: string;
}) => {
    const reducerFn = (acc: number, po: Pick<ProductOrderFormValues, 'unitCost' | 'count'>) =>
        sumNumbersAsDecimals(acc, multiplyNumbersAsDecimals(po.unitCost || 0, po.count || 0));

    if (hasSingleCurrency) {
        return (formValues.productOrders || []).reduce(reducerFn, formValues.value || 0);
    }

    const productsInDefaultCurrency = pipe(
        formValues.productOrders || [],
        array.map((po) => ({
            count: po.count,
            unitCost: getCostInDefaultCurrency(po.unitCost || 0, po.productCurrency, exchangeRates),
        }))
    );

    const serviceAmountInDefaultCurrency = getCostInDefaultCurrency(
        formValues.value || 0,
        serviceCurrency,
        exchangeRates
    );

    return productsInDefaultCurrency.reduce(reducerFn, serviceAmountInDefaultCurrency);
};
